import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Calendar, DollarSign, TrendingUp, ArrowUpDown, ChevronLeft, ChevronRight, X, ChevronDown, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea,
} from 'recharts';
import { TruncatedText } from '../components/ui/truncated-text';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function IncomeHistory() {
  const { t } = useTranslation('incomeHistory');
  const FULL_MONTHS = useMemo(() => t('common:months', { returnObjects: true }), [t]);
  const MONTHS = useMemo(() => t('common:monthsShort', { returnObjects: true }), [t]);
  const [incomes, setIncomes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedProjects, setSelectedProjects] = useState(new Set()); // empty = all
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const projectDropdownRef = useRef(null);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  // Grafana-style drag-to-select month range on the chart.
  // monthRange = {start, end} (1-based, inclusive) or null.
  const [monthRange, setMonthRange] = useState(null);
  const [refLeft, setRefLeft] = useState(null);   // month label while dragging
  const [refRight, setRefRight] = useState(null);

  // Currency display toggle
  const [displayCurrency, setDisplayCurrency] = useState('USD');
  const [exchangeRate, setExchangeRate] = useState(null);

  // Client-side pagination for table
  const [tablePage, setTablePage] = useState(1);
  const [tablePerPage, setTablePerPage] = useState(10);

  // Fetch exchange rate
  useEffect(() => {
    const fetchRate = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/exchange-rates/live`, { withCredentials: true });
        if (res.data?.rate) setExchangeRate(res.data.rate);
      } catch (e) {
        console.error('Error fetching exchange rate:', e);
      }
    };
    fetchRate();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('year', selectedYear);
      params.append('per_page', '1000');
      params.append('page', '1');

      const [incomesRes, projectsRes] = await Promise.all([
        axios.get(`${API_URL}/api/incomes?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/projects`, { withCredentials: true })
      ]);
      setIncomes(incomesRes.data.items || incomesRes.data || []);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Currency helpers
  const fmtUsd = (amount) => `$${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtUzs = (amount) => `${Math.round(amount || 0).toLocaleString()} UZS`;
  const fmtOriginal = (amount, currency) => currency === 'UZS' ? fmtUzs(amount) : fmtUsd(amount);

  const displayAmount = useCallback((usdAmount) => {
    if (displayCurrency === 'UZS' && exchangeRate) {
      return fmtUzs(usdAmount * exchangeRate);
    }
    return fmtUsd(usdAmount);
  }, [displayCurrency, exchangeRate]);

  const displayRaw = useCallback((usdAmount) => {
    if (displayCurrency === 'UZS' && exchangeRate) {
      return usdAmount * exchangeRate;
    }
    return usdAmount;
  }, [displayCurrency, exchangeRate]);

  // Prepare chart data from all incomes (filtered by project)
  const chartData = useMemo(() => {
    const monthlyData = {};
    for (let i = 1; i <= 12; i++) {
      monthlyData[i] = { month: MONTHS[i - 1], monthNum: i, total: 0 };
    }

    const filtered = selectedProjects.size === 0
      ? incomes
      : incomes.filter(inc => selectedProjects.has(inc.project_id));

    filtered.forEach(income => {
      const month = income.month;
      if (monthlyData[month]) {
        const val = (displayCurrency === 'UZS' && exchangeRate)
          ? (income.currency === 'UZS' ? (income.amount || 0) : (income.converted_amount || income.amount || 0) * exchangeRate)
          : (income.converted_amount || income.amount || 0);
        monthlyData[month].total += val;
      }
    });

    const all = Object.values(monthlyData);
    // When a range is applied, the chart zooms to just that range.
    if (monthRange) return all.filter((m) => m.monthNum >= monthRange.start && m.monthNum <= monthRange.end);
    return all;
  }, [incomes, selectedProjects, displayRaw, displayCurrency, exchangeRate, monthRange, MONTHS]);

  // Filtered + sorted incomes for table.
  // A chart drag-range (monthRange) takes precedence over the single-month Select.
  const sortedIncomes = useMemo(() => {
    return [...incomes]
      .filter(inc => selectedProjects.size === 0 || selectedProjects.has(inc.project_id))
      .filter(inc => {
        if (monthRange) return inc.month >= monthRange.start && inc.month <= monthRange.end;
        return selectedMonth === 'all' || inc.month === parseInt(selectedMonth);
      })
      .sort((a, b) => {
        if (sortBy === 'month') {
          if (a.month !== b.month) return a.month - b.month;
          return new Date(b.created_at) - new Date(a.created_at);
        }
        return new Date(b.created_at) - new Date(a.created_at);
      });
  }, [incomes, selectedProjects, selectedMonth, sortBy, monthRange]);

  // Commit the in-progress drag selection to an actual month-range filter.
  const finishRangeSelect = useCallback(() => {
    if (refLeft && refRight && refLeft !== refRight) {
      // short month labels (current language) -> 1-based month numbers
      const a = MONTHS.indexOf(refLeft) + 1;
      const b = MONTHS.indexOf(refRight) + 1;
      if (a > 0 && b > 0) {
        setMonthRange({ start: Math.min(a, b), end: Math.max(a, b) });
        setSelectedMonth('all'); // range supersedes the single-month select
      }
    }
    setRefLeft(null);
    setRefRight(null);
  }, [refLeft, refRight, MONTHS]);

  const clearMonthRange = () => { setMonthRange(null); setRefLeft(null); setRefRight(null); };

  // Always USD — used for corner "USD: $XX" indicator
  const totalIncomeUsd = sortedIncomes.reduce((sum, inc) => sum + (inc.converted_amount || inc.amount || 0), 0);
  // Display-currency-aware total: UZS records use original amount, USD records convert at current rate
  const totalIncomeDisplay = (displayCurrency === 'UZS' && exchangeRate)
    ? sortedIncomes.reduce((sum, inc) => sum + (inc.currency === 'UZS' ? (inc.amount || 0) : (inc.converted_amount || inc.amount || 0) * exchangeRate), 0)
    : totalIncomeUsd;

  // Client-side pagination
  const totalTableItems = sortedIncomes.length;
  const totalTablePages = Math.ceil(totalTableItems / tablePerPage);
  const paginatedIncomes = sortedIncomes.slice((tablePage - 1) * tablePerPage, tablePage * tablePerPage);
  const startItem = totalTableItems === 0 ? 0 : (tablePage - 1) * tablePerPage + 1;
  const endItem = Math.min(tablePage * tablePerPage, totalTableItems);

  useEffect(() => { setTablePage(1); }, [selectedProjects, selectedMonth, sortBy, tablePerPage, monthRange]);

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.project_id === projectId);
    return project ? project.name : t('unknownProject');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return t('notAvailable');
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatIncomeDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const hasActiveFilters = selectedProjects.size > 0 || selectedMonth !== 'all' || monthRange !== null;
  const clearFilters = () => { setSelectedProjects(new Set()); setSelectedMonth('all'); clearMonthRange(); };

  const toggleProject = (projectId) => {
    setSelectedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  const projectFilterLabel = selectedProjects.size === 0
    ? t('filters.allProjects')
    : selectedProjects.size === 1
      ? getProjectName([...selectedProjects][0])
      : t('filters.projectsSelected', { count: selectedProjects.size });

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(e.target)) {
        setProjectDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  const chartLabel = displayCurrency === 'UZS' ? 'UZS' : '$';

  return (
    <div className="space-y-6" data-testid="income-history-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-100 tracking-tight" style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
            {t('title')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
            {t('subtitle')}
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">{t('common:fields.year')}</Label>
            <Select value={selectedYear.toString()} onValueChange={(val) => setSelectedYear(parseInt(val))}>
              <SelectTrigger className="w-[120px]" data-testid="year-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">{t('common:fields.month')}</Label>
            <Select value={selectedMonth} onValueChange={(v) => { setSelectedMonth(v); setMonthRange(null); }}>
              <SelectTrigger className="w-[140px]" data-testid="month-filter"><SelectValue placeholder={t('filters.allMonths')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('filters.allMonths')}</SelectItem>
                {FULL_MONTHS.map((month, idx) => (
                  <SelectItem key={idx} value={(idx + 1).toString()}>{month}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div ref={projectDropdownRef} className="relative">
            <Label className="text-xs text-slate-500 mb-1 block">{t('common:fields.project')}</Label>
            <button
              data-testid="project-filter"
              onClick={() => setProjectDropdownOpen(o => !o)}
              className="flex items-center justify-between w-[200px] h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              <span className="truncate">{projectFilterLabel}</span>
              <ChevronDown className="h-4 w-4 ml-2 shrink-0 text-slate-400" />
            </button>
            {projectDropdownOpen && (
              <div className="absolute z-50 mt-1 w-[220px] rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg py-1 max-h-64 overflow-auto">
                <button
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
                  onClick={() => setSelectedProjects(new Set())}
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${selectedProjects.size === 0 ? 'bg-emerald-600 border-emerald-600' : 'border-slate-300 dark:border-slate-600'}`}>
                    {selectedProjects.size === 0 && <Check className="h-3 w-3 text-white" />}
                  </div>
                  {t('filters.allProjects')}
                </button>
                <div className="border-t border-slate-100 dark:border-slate-700 my-1" />
                {projects.map(project => (
                  <button
                    key={project.project_id}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
                    onClick={() => toggleProject(project.project_id)}
                  >
                    <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${selectedProjects.has(project.project_id) ? 'bg-emerald-600 border-emerald-600' : 'border-slate-300 dark:border-slate-600'}`}>
                      {selectedProjects.has(project.project_id) && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <span className="truncate">{project.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">{t('filters.sortBy')}</Label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[150px]" data-testid="sort-filter">
                <ArrowUpDown className="h-4 w-4 mr-2" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">{t('filters.sortDate')}</SelectItem>
                <SelectItem value="month">{t('filters.sortMonth')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Currency Toggle */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">{t('filters.display')}</Label>
            <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden h-10">
              <button
                className={`px-3 text-sm font-medium transition-colors ${displayCurrency === 'USD' ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                onClick={() => setDisplayCurrency('USD')}
              >$ USD</button>
              <button
                className={`px-3 text-sm font-medium transition-colors ${displayCurrency === 'UZS' ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                onClick={() => setDisplayCurrency('UZS')}
              >UZS</button>
            </div>
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-10 text-slate-500">
              <X className="h-4 w-4 mr-1" /> {t('filters.clear')}
            </Button>
          )}
        </div>
      </div>

      {/* Summary Card */}
      <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="h-6 w-6" />
              <span className="text-emerald-100">{t('summary.totalIncome', { year: selectedYear })}</span>
            </div>
            <div className="text-4xl font-bold">{displayCurrency === 'UZS' && exchangeRate ? fmtUzs(totalIncomeDisplay) : fmtUsd(totalIncomeDisplay)}</div>
            <div className="text-emerald-100 mt-1">{t('summary.records', { count: sortedIncomes.length })}</div>
          </div>
          {displayCurrency === 'UZS' && exchangeRate && (
            <div className="text-right text-emerald-200 text-xs">
              <div>{t('summary.rate', { rate: Number(exchangeRate).toLocaleString() })}</div>
              <div className="mt-0.5">USD: {fmtUsd(totalIncomeUsd)}</div>
            </div>
          )}
        </div>
      </div>

      {/* Income by Month Chart */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
        <div className="flex items-center justify-between gap-2 mb-6 flex-wrap">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-600" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{t('chart.title')}</h2>
          </div>
          {monthRange ? (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 px-3 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-400" data-testid="month-range-chip">
                {FULL_MONTHS[monthRange.start - 1]} – {FULL_MONTHS[monthRange.end - 1]}
              </span>
              <button onClick={clearMonthRange} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" data-testid="clear-month-range">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <span className="text-xs text-slate-400 dark:text-slate-500 select-none">
              {t('chart.dragHint')}
            </span>
          )}
        </div>

        <div className="h-80 select-none">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              onMouseDown={(e) => { if (e && e.activeLabel) { setRefLeft(e.activeLabel); setRefRight(e.activeLabel); } }}
              onMouseMove={(e) => { if (refLeft && e && e.activeLabel) setRefRight(e.activeLabel); }}
              onMouseUp={finishRangeSelect}
              onMouseLeave={finishRangeSelect}
              style={{ cursor: 'crosshair' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" stroke="#64748b" />
              <YAxis stroke="#64748b" tickFormatter={(val) => displayCurrency === 'UZS' ? `${(val / 1000000).toFixed(1)}M` : `$${val.toLocaleString()}`} />
              <Tooltip
                formatter={(value) => [displayCurrency === 'UZS' ? `${Math.round(value).toLocaleString()} UZS` : `$${value.toLocaleString()}`, t('chart.series')]}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
              />
              <Bar dataKey="total" fill="#059669" radius={[4, 4, 0, 0]} name={t('chart.series')} />
              {refLeft && refRight && (
                <ReferenceArea
                  x1={refLeft}
                  x2={refRight}
                  stroke="#059669"
                  strokeOpacity={0.9}
                  strokeWidth={1.5}
                  fill="#059669"
                  fillOpacity={0.28}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Summary row */}
        {(() => {
          const nonZero = chartData.filter(m => m.total > 0);
          const total = chartData.reduce((s, m) => s + m.total, 0);
          const maxMonth = nonZero.length > 0 ? nonZero.reduce((max, m) => m.total > max.total ? m : max, nonZero[0]) : null;
          if (total === 0) return null;
          const avg = total / (nonZero.length || 1);
          return (
            <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-xs text-slate-500">{t('chart.total', { year: selectedYear })}</div>
                <div className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                  {displayCurrency === 'UZS' && exchangeRate
                    ? `${Math.round(total).toLocaleString()} UZS`
                    : `$${total.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">{t('chart.monthlyAvg')}</div>
                <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  {displayCurrency === 'UZS' && exchangeRate
                    ? `${Math.round(avg).toLocaleString()} UZS`
                    : `$${avg.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">{t('chart.highestMonth')}</div>
                <div className="text-sm font-semibold text-emerald-600">
                  {maxMonth ? FULL_MONTHS[parseInt(maxMonth.monthNum) - 1] : '—'}
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Income Records Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-emerald-600" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{t('table.title')}</h2>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {sortBy === 'date' ? t('table.sortedByDate') : t('table.sortedByMonth')}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('table.incomeDate')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('common:fields.project')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('table.period')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('common:fields.description')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('table.original')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('table.value', { currency: displayCurrency })}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('table.recorded')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {paginatedIncomes.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                    {t('table.empty', { year: selectedYear })}
                  </td>
                </tr>
              ) : (
                paginatedIncomes.map((income) => (
                  <tr key={income.income_id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30" data-testid={`income-row-${income.income_id}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {formatIncomeDate(income.income_date) || formatDate(income.created_at)?.split(',')[0]}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900 dark:text-slate-100">{getProjectName(income.project_id)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 rounded-full">
                        {FULL_MONTHS[income.month - 1]} {income.year}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <TruncatedText text={income.description} maxLength={35} className="text-sm text-slate-600 dark:text-slate-400 max-w-[200px] truncate block" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {fmtOriginal(income.amount, income.currency)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                        {income.currency === 'UZS' ? fmtUzs(income.amount) : displayAmount(income.converted_amount || income.amount)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-slate-500">{formatDate(income.created_at)}</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {totalTableItems > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {t('pagination.showing', { start: startItem, end: endItem, total: totalTableItems })}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">{t('pagination.rows')}</span>
                <Select value={tablePerPage.toString()} onValueChange={(val) => setTablePerPage(parseInt(val))}>
                  <SelectTrigger className="w-[70px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" onClick={() => setTablePage(p => Math.max(1, p - 1))} disabled={tablePage <= 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: Math.min(totalTablePages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalTablePages <= 5) pageNum = i + 1;
                  else if (tablePage <= 3) pageNum = i + 1;
                  else if (tablePage >= totalTablePages - 2) pageNum = totalTablePages - 4 + i;
                  else pageNum = tablePage - 2 + i;
                  return (
                    <Button key={pageNum} variant={tablePage === pageNum ? 'default' : 'outline'} size="sm" onClick={() => setTablePage(pageNum)} className="w-8 h-8 p-0">
                      {pageNum}
                    </Button>
                  );
                })}
                <Button variant="outline" size="sm" onClick={() => setTablePage(p => Math.min(totalTablePages, p + 1))} disabled={tablePage >= totalTablePages}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default IncomeHistory;
